import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { fetchData } from "../api/utils";
import axios from "axios";
import Footer from "../components/Shared/Footer";
import { getUserId, getAccesstoken } from "../utils/auth";

const ProductDetails = () => {
  const user_id = getUserId();
  const access_token = getAccesstoken();
  
  const { productId } = useParams();
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [cart, setCart] = useState([]);
  const [cartCount, setCartCount] = useState(0); // New state for cart count

  // Fetch product details based on the productId
  const fetchProductDetails = async () => {
    try {
      const productData = await fetchData(`products/${productId}`, {
        schema: "public",
      });
      setProduct(productData);
    } catch (error) {
      console.log(error);
    }
  };

  const handleAddToCart = async () => {
    if (!product) {
      alert("Product data is missing.");
      return;
    }

    const cartItem = {
      productId: product.id,
      quantity: quantity,
      unit_price: product.price * quantity,
      name: product.name,
    };

    if (user_id && access_token) {
      try {
        // Fetch the existing cart item for the user and product
        const response = await axios.get(
          `${baseUrl}/cart_items/filter_by_user_id`,
          {
            params: { user_id, schema: "public" },
            headers: { Authorization: `Bearer ${access_token}` },
          }
        );

        const existingCartItem = response.data.length > 0 ? response.data[0] : null;

        if (existingCartItem) {
          // Update the quantity and price of the existing cart item
          const updatedCartData = {
            ...existingCartItem,
            quantity: existingCartItem.quantity + quantity,
            unit_price: product.price * (existingCartItem.quantity + quantity),
            updated_at: new Date().toISOString(),
          };

          await axios.put(
            `${baseUrl}/cart_items?id=${existingCartItem.id}`,
            updatedCartData,
            {
              params: { schema: "public" },
              headers: { Authorization: `Bearer ${access_token}` },
            }
          );

          alert("Cart item updated!");
        } else {
          // Add a new cart item if not found
          const cartData = {
            user_id,
            product_id: product.id,
            quantity: quantity,
            unit_price: product.price * quantity,
            created_at: new Date().toISOString(),
          };

          await axios.post(
            `${baseUrl}/cart_items/`,
            cartData,
            {
              params: { schema: "public" },
              headers: { Authorization: `Bearer ${access_token}` },
            }
          );

          alert("Product added to cart!");
        }

        // Update the local cart state and count
        const updatedCart = existingCartItem
          ? cart.map((item) =>
            item.productId === product.id
              ? {
                ...item,
                quantity: item.quantity + quantity,
                unit_price: product.price * (item.quantity + quantity),
              }
              : item
          )
          : [...cart, cartItem];

        setCart(updatedCart);
        setCartCount(updatedCart.reduce((total, item) => total + item.quantity, 0));
        localStorage.setItem("cart", JSON.stringify(updatedCart));
      } catch (error) {
        if (error.response && error.response.status === 404) {
          // Handle the case where the cart item is not found
          const cartData = {
            user_id,
            product_id: product.id,
            quantity: quantity,
            unit_price: product.price * quantity,
            created_at: new Date().toISOString(),
          };

          await axios.post(
            `${baseUrl}/cart_items/`,
            cartData,
            {
              params: { schema: "public" },
              headers: { Authorization: `Bearer ${access_token}` },
            }
          );

          alert("Product added to cart!");
        } else {
          console.log(error);
          alert("An error occurred while handling the cart.");
        }
      }
    } else {
      // Local cart handling for guest users
      const existingCartItemIndex = cart.findIndex((item) => item.productId === product.id);
      if (existingCartItemIndex === -1) {
        const updatedCart = [...cart, cartItem];
        setCart(updatedCart);
        setCartCount(cartCount + quantity);
        localStorage.setItem("cart", JSON.stringify(updatedCart));
        alert("Product added to cart (guest mode)!");
      } else {
        const updatedCart = [...cart];
        updatedCart[existingCartItemIndex].quantity += quantity;
        updatedCart[existingCartItemIndex].unit_price =
          updatedCart[existingCartItemIndex].quantity * product.price;
        setCart(updatedCart);
        setCartCount(cartCount + quantity);
        localStorage.setItem("cart", JSON.stringify(updatedCart));
        alert("Cart item updated (guest mode)!");
      }
    }
  };


  // Fetch product details and add-on services on component mount
  useEffect(() => {
    fetchProductDetails();
  }, [productId]);


  useEffect(() => {
    const storedCart = JSON.parse(localStorage.getItem("cart"));
    if (storedCart) {
      setCart(storedCart);
      setCartCount(storedCart.reduce((total, item) => total + item.quantity, 0)); // Update cart count based on stored data
    }
  }, []);

  // Functions to handle quantity changes
  const increaseQuantity = () => {
    setQuantity((prevQuantity) => prevQuantity + 1);
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity((prevQuantity) => prevQuantity - 1);
    }
  };

  if (!product) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <section className="breadcrumb-section pt-0">
        <div className="container-fluid-lg">
          <div className="row">
            <div className="col-12">
              <div className="breadcrumb-contain">
                <h2>Product Details</h2>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <a href="/">
                        <i className="fa-solid fa-house"></i>
                      </a>
                    </li>
                    <li className="breadcrumb-item active">Product Details</li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="product-section" style={{ marginBottom: '100px' }}>
        <div className="container-fluid-lg">
          <div className="row">
            {/* Image Section */}
            <div className="col-xl-4 col-lg-5 col-md-6 wow fadeInUp">
              <div className="product-left-box">
                <div className="row g-sm-4 g-2">
                  <div className="col-12">
                    <div className="product-main no-arrow">
                      <div>
                        <div className="product-image">
                          <div className="custom-image-container fresh-image-2">
                            <img
                              src={`../assets/images/products/${product.product_image}`}
                              alt={product.name}
                              className="custom-image blur-up-img lazyloaded-img rounded-corners"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Details Section */}
            <div className="col-xl-8 col-lg-7 col-md-6 wow fadeInUp">
              <div className="right-box-contain">
                <h2 className="theme-color name">{product.name}</h2>
                <div className="price-rating">
                  <h3 className="theme-color price">
                    ₹{product.price}
                  </h3>
                </div>

                <div className="product-contain">
                  <p className="w-100">{product.description}</p>
                </div>

                {/* Variant Selection */}
                {/* <div className="product-package">
                  <div className="product-title">
                    <h4>Variant</h4>
                  </div>
                  <ul className="image select-package">
                    <li className="form-check">
                      <input
                        className="form-check-input"
                        checked
                        type="radio"
                        name="size"
                        id="him"
                      />
                      <label className="form-check-label" htmlFor="small">
                        <img src="../assets/images/fruit/product/5.png" alt="" />
                      </label>
                    </li>
                    <li className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="size"
                        id="her"
                      />
                      <label className="form-check-label" htmlFor="medium">
                        <img src="../assets/images/fruit/product/6.png" alt="" />
                      </label>
                    </li>
                  </ul>
                </div> */}

                {/* Quantity Controls */}
                <div className="note-box product-package">
                  <div className="cart_qty qty-box product-qty">
                    <div className="input-group">
                      <button
                        type="button"
                        className="qty-left-minus"
                        onClick={decreaseQuantity}
                      >
                        <i className="fa fa-minus"></i>
                      </button>
                      <input
                        className="form-control input-number qty-input"
                        type="text"
                        value={quantity}
                        readOnly
                      />
                      <button
                        type="button"
                        className="qty-right-plus"
                        onClick={increaseQuantity}
                      >
                        <i className="fa fa-plus"></i>
                      </button>
                    </div>
                  </div>

                  <div className="w-100">
                    <button
                      onClick={() => handleAddToCart(product.id)}
                      className="btn btn-md cart-button text-white add-to-cart-btn"
                    >
                      ADD TO CART
                    </button>
                  </div>
                </div>

                {product.additional_details &&
                  product.additional_details.offerings &&
                  product.additional_details.offerings.length > 0 && (
                    <div className="add-on-services mt-3">
                      <h5>Offerings:</h5>
                      <ul>
                        {product.additional_details.offerings.map((offering, index) => (
                          <li key={index}>
                            <div
                              className="font-sm fas fa-check"
                              style={{ color: "#75C043" }}
                            ></div>{" "}
                            {offering.name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default ProductDetails;
